import React, {
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useReducer,
  useState,
  createContext,
  useRef
} from 'react';
import useTranslations from '../../../hooks/useTranslations';
import { compareTypeMap } from '../../../internal-components/conditional-logic/compareTypeMap';
import { defaultComparisonMap } from '../../../internal-components/conditional-logic/defaultComparisonMap';
import { getAllFields } from '../../../internal-components/form-builder/getAllFields';
import generateId from '../../../utils/generateId';
import FieldsQueryBuilderView from '../view/FieldsQueryBuilderView';
import { ConditionsQueryBuilderContext } from './ConditionsQueryBuilderEngine';

export const FieldsQueryBuilderContext = createContext({});

const FieldsQueryBuilderReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const initialState = {
  fieldValue: '',
  internOp: '',
  conditions: {}
};

export const filterConditionalTypes = ({ uiType }) =>
  Object.keys(compareTypeMap).includes(uiType);

/**
 *
 * @param {Array} query - Query of field type
 * @param {Func} update - Callback to return the updated query created by this component
 * @param {String} sentencePrefix - A default sentence to place before selecting the field
 */
const FieldsQueryBuilderEngine = ({
  form: givenForm,
  useStaticFieldId,
  query = [],
  update = () => {},
  sentencePrefix,
  fields2Filter = [],
  variant: givenVariant
}) => {
  const t = useTranslations();
  const [data, dispatch] = useReducer(FieldsQueryBuilderReducer, initialState);
  const [comparisonType, _setComparisonType] = useState();
  const [inDeleteSequence, setInDeleteSequence] = useState(null);
  const [hoverOn, setHoverOn] = useState(null);
  const contextValues = useContext(ConditionsQueryBuilderContext);
  const form = givenForm || contextValues.form;
  const variant = givenVariant || contextValues.variant;
  const skipUpdate = useRef(true);

  useEffect(() => {
    if (skipUpdate.current) {
      skipUpdate.current = false;
      return;
    }

    const allSubConditions = Object.values(data.conditions);
    const parsedData = allSubConditions.map(conditions => ({
      field: `\${${data.fieldValue}}`,
      internOp: data.internOp,
      ...conditions
    }));

    update(parsedData);
  }, [data]);

  const fields = useMemo(() => getAllFields(form), [form]);
  const filterFields = useCallback(
    ({ internalId }) => !fields2Filter.includes(internalId),
    [(fields2Filter = [])]
  );

  useEffect(() => {
    const qBlock = query[0] || {};
    const fieldId =
      useStaticFieldId ||
      (qBlock.field || '').replace(/\${/g, '').replace(/}/g, '');
    const internOp = qBlock.internOp || 'and';
    const intervalCount = qBlock.intervalCount || null;
    const intervalUnit = qBlock.intervalUnit || null;
    const secondIntervalCount = qBlock.secondIntervalCount || null;
    const secondIntervalUnit = qBlock.secondIntervalUnit || null;
    const secondDate = qBlock.secondDate || null;
    const conditions = {};

    fields.find(f => f.internalId === fieldId) &&
      query.forEach(qBlock => {
        const newCondition = {
          value: qBlock.value,
          op: qBlock.op
        };
        if (intervalCount) {
          newCondition.intervalCount = intervalCount;
        }
        if (intervalUnit) {
          newCondition.intervalUnit = intervalUnit;
        }
        if (secondIntervalCount) {
          newCondition.secondIntervalCount = secondIntervalCount;
        }
        if (secondIntervalUnit) {
          newCondition.secondIntervalUnit = secondIntervalUnit;
        }
        if (secondDate) {
          newCondition.secondDate = secondDate;
        }
        conditions[`id-${generateId()}`] = newCondition;
      });

    setComparisonType(fieldId);
    skipUpdate.current = true;
    dispatch({
      type: 'update',
      payload: { fieldValue: fieldId, internOp, conditions }
    });
  }, []);

  const setComparisonType = useCallback(
    id => {
      const field = fields.find(f => f.internalId === id) || {};
      _setComparisonType(compareTypeMap[field.uiType]);
    },
    [fields]
  );

  const inputFieldChoices = useMemo(
    () =>
      fields
        // only supported logic types
        .filter(filterConditionalTypes)
        .filter(filterFields)
        // don't show archived fields
        .filter(field => !field.archived),
    [fields]
  );

  const updateField = useCallback(
    value => {
      const { uiType } = fields.find(f => f.internalId === value);
      const compareType = compareTypeMap[uiType];

      const conditions = {};
      conditions[`id-${generateId()}`] = defaultComparisonMap[compareType];

      dispatch({
        type: 'update',
        payload: {
          fieldValue: value,
          conditions
        }
      });
      setComparisonType(value);
    },
    [fields]
  );

  const updateConditions = useCallback(
    (id, conditions, compareType) => {
      const conditionsCpy = JSON.parse(JSON.stringify(data.conditions));
      conditionsCpy[id] = conditions;
      if (conditions.value && !conditions.op) {
        conditionsCpy[id].op = defaultComparisonMap[compareType].op;
      }
      dispatch({ type: 'update', payload: { conditions: conditionsCpy } });
    },
    [data]
  );

  const updateInternOp = useCallback(value => {
    dispatch({ type: 'update', payload: { internOp: value } });
  }, []);

  const addCondition = useCallback(() => {
    const conditionsCpy = JSON.parse(JSON.stringify(data.conditions));
    conditionsCpy[`id-${generateId()}`] = defaultComparisonMap[comparisonType];
    dispatch({ type: 'update', payload: { conditions: conditionsCpy } });
  }, [data]);

  const removeCondition = useCallback(
    id => {
      const conditionsCpy = JSON.parse(JSON.stringify(data.conditions));
      delete conditionsCpy[id];
      dispatch({ type: 'update', payload: { conditions: conditionsCpy } });
      setInDeleteSequence(null);
    },
    [data]
  );

  const fieldName = useMemo(() => {
    const fNode = inputFieldChoices.find(f => f.internalId === data.fieldValue);
    if (fNode) {
      return fNode.title || `Untitled ${fNode.uiType} field`;
    }
    return '';
  }, [data, inputFieldChoices]);

  const contextProp = {
    fieldValue: data.fieldValue,
    fields,
    form,
    variant
  };

  const viewProps = {
    query,
    useStaticFieldId,
    sentencePrefix: sentencePrefix || t('logic.when_the_answer_to'),
    inputFieldChoices,
    updateField,
    updateInternOp,
    fieldValue: data.fieldValue,
    conditions: data.conditions,
    internOp: data.internOp,
    fieldName,
    comparisonType,
    setHoverOn,
    hoverOn,
    addCondition,
    removeCondition,
    setInDeleteSequence,
    inDeleteSequence,
    updateConditions,
    variant
  };

  return (
    <FieldsQueryBuilderContext.Provider value={contextProp}>
      <FieldsQueryBuilderView {...viewProps} />
    </FieldsQueryBuilderContext.Provider>
  );
};

export default FieldsQueryBuilderEngine;
